import {
  createUpdateInfiniteDataRecipe,
  createUpdateMultipleItemsRecipe,
  createUpdateSingleItemRecipe,
} from '@autone/utils';

import { type TransformedAssortmentProductLocationsResponse } from '../schemas/get-assortment-product-locations.schema';
import { type TransformedAssortmentSkuLocationsResponse } from '../schemas/get-assortment-sku-locations.schema';
import { type EditAssortmentSkuLocationRequest } from '../types/assortment-edit-sku-location.types';

import { type RowIdentifiersLoc, type RowIdentifiersSkuLoc } from './types';

type SkuLocationUpdateType =
  EditAssortmentSkuLocationRequest['body']['assortment_update']['update_type'];

export const createSetProductLocationsLoadingRecipe = (
  { locationId }: RowIdentifiersLoc,
  updateType: SkuLocationUpdateType,
) =>
  createUpdateInfiniteDataRecipe(
    createUpdateSingleItemRecipe<TransformedAssortmentProductLocationsResponse>(
      {
        predicate: (item) => item.location.id === locationId,
        updateRecipe: (item) => {
          switch (updateType) {
            case 'is_assorted': {
              item.sku_counts.awaiting_requests += 1;
              break;
            }
            case 'initial_allocation': {
              item.initial_allocation.awaiting_requests += 1;
              break;
            }
          }
        },
      },
    ),
  );

export const createResetProductLocationsLoadingRecipe = (
  { locationId }: RowIdentifiersLoc,
  updateType: SkuLocationUpdateType,
) =>
  createUpdateInfiniteDataRecipe(
    createUpdateSingleItemRecipe<TransformedAssortmentProductLocationsResponse>(
      {
        predicate: (item) => item.location.id === locationId,
        updateRecipe: (item) => {
          switch (updateType) {
            case 'is_assorted': {
              item.sku_counts.awaiting_requests -= 1;
              break;
            }
            case 'initial_allocation': {
              item.initial_allocation.awaiting_requests -= 1;
              break;
            }
          }
        },
      },
    ),
  );

export const createDisableProductLocationSkusOpposingInputRecipe = (
  { locationId, skuId }: RowIdentifiersSkuLoc,
  updateType: SkuLocationUpdateType,
) =>
  createUpdateInfiniteDataRecipe(
    createUpdateSingleItemRecipe<TransformedAssortmentSkuLocationsResponse>({
      predicate: (item) =>
        item.location.id === locationId && item.sku.id === skuId,
      updateRecipe: (item) =>
        updateType === 'is_assorted'
          ? (item.initial_allocation.is_disabled = true)
          : (item.is_assorted.is_disabled = true),
    }),
  );

export const createResetDisableProductLocationSkusRecipe = ({
  locationId,
  skuId,
}: RowIdentifiersSkuLoc) =>
  createUpdateInfiniteDataRecipe(
    createUpdateSingleItemRecipe<TransformedAssortmentSkuLocationsResponse>({
      predicate: (item) =>
        item.location.id === locationId && item.sku.id === skuId,
      updateRecipe: (item) => {
        item.initial_allocation.is_disabled = false;
        item.is_assorted.is_disabled = false;
      },
    }),
  );

export const createSetAllProductLocationSkusLoadingRecipe = () =>
  createUpdateInfiniteDataRecipe(
    createUpdateMultipleItemsRecipe<TransformedAssortmentSkuLocationsResponse>({
      predicate: () => true,
      updateRecipe: (item) => {
        item.is_assorted.awaiting_requests += 1;
      },
    }),
  );

export const createResetAllProductLocationSkusLoadingRecipe = () =>
  createUpdateInfiniteDataRecipe(
    createUpdateMultipleItemsRecipe<TransformedAssortmentSkuLocationsResponse>({
      predicate: () => true,
      updateRecipe: (item) => {
        item.is_assorted.awaiting_requests -= 1;
      },
    }),
  );
